import React, { useContext, useEffect, useState } from "react";
import classes from "./Step2.module.css";
import { Urls } from "../../../../../Urls";
import axios from "../../../../../Axios/Axios";
import Loader from "../../../../Loader/Loader";
import Switch from "react-switch";
import { toast } from "react-toastify";
import { saveMastersApi } from "../../../Utility";
import mixpanel from "mixpanel-browser";
import { UserApiContext } from "../../../../UserDetailsContext/UserDetailsContext";
//import AutoComplete from "../../../../CompanyProfileNew/FreightMaster/FreightMaster/UploadExcel/AutoComplete/AutoComplete";
import AutoComplete from "../../../../Shared/AutosuggestV2/Autosuggestv2";
import { Form } from "react-bootstrap";
import { sendMailToMasters } from "../../Utility/utility";
import { MyCompanyProfileContext } from "../../../../../../src/MyCompanyProfileContext";
import { useMixPanelForTracking } from "../../../../../utils/TrackMixPanel";

const BATCH_SIZE = 100;
const MAX_CONCURRENT_REQUESTS = 10;

/**
 * Step 2 Location Review step
 * Component for displaying and reviewing location details in step2.
 *
 * @param {number} step - current step
 * @param {React.SetStateAction} setStep - sets the current step
 * @param {Array<Object>} uploadedData - Array of uploaded location data
 * @param {React.SetStateAction} setUploadedData - Function to update the uploaded data
 * @param {Array<Object>} locationMasterList - List of master locations
 * @param {React.SetStateAction} setLocationMasterList - Function to update the master location list
 * @returns {JSX.Element} rendered LocationDetails component
 */
const LocationDetails = ({
  uploadedData,
  setLocationMasterList,
  locationMasterList = [],
  company_id,
  emailTemplate,
  superAdmins,
  selectedOrg = "company",
  onClose
}) => {
  // const [suggestedLocation, setSuggestedLocation] = useState([])
  const [_uploadedData, set_uploadedData] = useState([]);
  const [show, setShow] = useState(false);
  const [autoGenerateCode, setAutoGenerateCode] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Fetching locations...");
  const { userDetails } = useContext(UserApiContext);
  const { companySettingData } = useContext(MyCompanyProfileContext);
  const { TrackMixPanel } = useMixPanelForTracking(userDetails, companySettingData);

  /**
   * Processes a batch of location data and calls bulk autocomplete API.
   *
   * @param {Array} batch - array of location data.
   * @returns {Promise<Object|null>} - response data or null.
   */
  const processLocationBatch = async (batch) => {
    const locationList = batch.map((data) => ({
      raw_key: data.key,
      raw_location: (data["Location*"] || "").trim(),
      raw_district: (data["District"] || "").trim(),
      raw_state: (data["State*"] || "").trim(),
      raw_location_code: (String(data["Location Code"]) || "").trim(),
    }));

    const payload = { location_list: locationList };

    try {
      const response = await axios.post(
        `${Urls.Autocomplete.Post.bulkSuggestion}?bulk_suggestions=true`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching bulk suggestions:", error);
      return null;
    }
  };

  /**
   * Effect hook to process uploaded location data by batching API calls
   * to the autocomplete service. This is done to avoid overloading server
   * and server timeout.
   */
  useEffect(() => {
    const processData = async () => {
      if (uploadedData && uploadedData.length > 0) {
        // Add a unique key to each data item to tracking of suggestions
        const dataWithKeys = uploadedData.map((data, index) => ({
          ...data,
          key: `loc_${index}`,
        }));

        const batches = [];
        // Split data into batches based on batch size
        for (let i = 0; i < dataWithKeys.length; i += BATCH_SIZE) {
          batches.push(dataWithKeys.slice(i, i + BATCH_SIZE));
        }

        const allSuggestions = {};

        /**
         * Processes a single batch of location data and collects suggestions.
         *
         * @param {Array} batch - batch of location data to process.
         */
        const processBatch = async (batch) => {
          const batchSuggestions = await processLocationBatch(batch);
          if (batchSuggestions) {
            // Map suggestions to their respective keys
            batchSuggestions.forEach((suggestion) => {
              allSuggestions[suggestion.raw_key] = suggestion;
            });
          }
        };

        setShow(true);
        // Process batches in parallel, by the maximum number of concurrent requests
        for (let i = 0; i < batches.length; i += MAX_CONCURRENT_REQUESTS) {
          const batchPromises = batches
            .slice(i, i + MAX_CONCURRENT_REQUESTS)
            .map(processBatch);
          await Promise.all(batchPromises);
          setLoadingMessage(`Processing...`); // Update loading message
        }
        setShow(false);

        // Update the uploaded data with suggestions retrieved from the API
        const updatedData = dataWithKeys.map((data) => ({
          ...data,
          selected_location: allSuggestions[data.key]?.value[0] || null,
        }));

        set_uploadedData(updatedData);
      }
    };

    processData();
  }, [uploadedData]); // Trigger effect when uploadedData changes

  /**
   * Handles the switch between manual and auto generation of location codes.
   *
   * @param {Event} e - event object for the switch component
   */
  const switchHandler = () => {
    setAutoGenerateCode(!autoGenerateCode);
    uniqueCode();
  };

  /**
   * Generates unique codes for locations without existing codes.
   */
  const uniqueCode = () => {
    if (autoGenerateCode === false) {
      const updatedData = _uploadedData.map((data) => {
        if (!data["Location Code"] || data["Location Code"] === "") {
          const code = parseInt(Date.now() * Math.random());
          const str = String(code).slice(0, 6).toUpperCase();
          return { ...data, "Location Code": parseInt(str) };
        }
        return data;
      });
      set_uploadedData(updatedData);
    }
  };

  /**
   * Function to handel change of location type
   * @param {Event} e - Event for dropdown component
   * @param {Array<Object>} data - uploaded data
   * @returns {Object} updated data
   */
  const LocationType = (e, index) => {
    const updatedData = [..._uploadedData];
    updatedData[index]["Location Type"] = e.target.value;
    set_uploadedData(updatedData);
  };

  /**
   * Saves the location details and updates the Location data state.
   * @param {Array<Object>} location_details - Array of location details to be saved
   */
  const save_details = async (location_details) => {
    setShow(true);
    const errorIndex = location_details.findIndex(
      (details) =>
        !details.selected_location ||
        !details.selected_location.location_name ||
        !details.selected_location.coordinates
    );

    if (errorIndex !== -1) {
      toast.error(
        `Please select location from dropdown. Error at row number ${errorIndex + 1}.`
      );
      setShow(false);
      return;
    }

    const buildPayload = (detailsArray) =>
      detailsArray.map((details) => ({
        addressLine1: details["Address"] ? String(details["Address"]) : "",
        addressLine2: "",
        location: details.selected_location,
        pincode: String(details?.["Pincode"] ?? ""),
        location_code: details["Location Code"]
          ? String(details["Location Code"])
          : "",
        type: details["Location Type"]
          ? String(details["Location Type"])
          : "Destination",
        customer_name: details["Customer Name"]
          ? String(details["Customer Name"])
          : "",
        gst_no: details["GST No"] || "",
      }));

    const fullPayload = buildPayload(location_details);

    try {
      const res = await saveMastersApi(
        company_id,
        "location",
        fullPayload,
        selectedOrg,
        TrackMixPanel
      );

      const apiErrors =
        res?.error ||
        res?.data?.error ||
        [];

      if (Array.isArray(apiErrors) && apiErrors.length > 0) {

        const _LM = [...uploadedData];

        apiErrors.forEach((item) => {
          _LM[item.index] = {
            ..._LM[item.index],
            isDuplicate: true,
            duplicateReason: item?.message,
          };
        });

        set_uploadedData(_LM);


        toast.error(
          "Location Codes are duplicates. Highlighted in red. Please fix and try again."
        )
        return
      }

      setLocationMasterList((prev) =>
        prev ? [...prev, ...fullPayload] : fullPayload
      );

      await sendMailToMasters(
        "Location Master",
        emailTemplate,
        superAdmins,
        { action: "New File Uploaded", lane: "" },
        setShow
      );

      toast.success("All locations added successfully");

      if (onClose) {
        onClose();
      } else {
        const closeButton = document.getElementById("close_contactus1");
        if (closeButton) closeButton.click();
      }

    } catch (err) {
      console.error("save error", err);
      toast.error("Error adding details");
    } finally {
      setShow(false);
    }
  };

  return (
    <>
      <Loader show={show} message={loadingMessage} />
      <div className="mt-4">
        <div className="d-flex justify-content-center align-items-center">
          <Form.Label className="mr-4">Location Code Generator:</Form.Label>
          <div style={{ gap: "5px" }} className="d-flex align-items-center">
            <span className="me-2">Manual</span>
            <Switch
              height={20}
              width={40}
              onChange={switchHandler}
              checked={autoGenerateCode}
            />
            <span className="ms-2">Auto</span>
          </div>
        </div>
        <div className={`table-responsive ${classes["table-width"]}`}>
          <table className={`table table-borderless mt-4 ${classes["table-width1"]}`}>
            <thead
              className="sticky-top"
              style={{ backgroundColor: "#eff1ff" }}
            >
              <tr className="">
                <th className="text-center">Sr No.</th>
                <th className="text-center">Location Code</th>
                <th className="text-center">Location*</th>
                <th className="text-center">District</th>
                <th className="text-center">State*</th>
                <th className="text-center">Pincode</th>
                <th className="text-center">Address</th>
                <th className="text-center">Location Type</th>
                <th className="text-center">Customer Name</th>
                <th className="text-center">GST No.</th>
                <th
                  className="text-center bg-light"
                  style={{
                    minWidth: "190px",
                    // position: "sticky",
                    // right: 0,
                    zIndex: 1,
                  }}
                >
                  <span>Cleaned Location</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {_uploadedData &&
                _uploadedData.map((data, index) => (
                  <tr key={index} className="position-relative"
                    style={data.isDuplicate ? { backgroundColor: "#f8d7da" } : {}}
                  >
                    <td className="text-center">{index + 1}.</td>
                    <td className="text-center">
                      {data["Location Code"] ? (
                        data["Location Code"]
                      ) : (
                        <input
                          min={0}
                          value={data["Location Code"] || ""}
                          readOnly={autoGenerateCode}
                          size="sm"
                          onKeyDown={(e) => {
                            if (e.key === "-") {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            const updatedData = [..._uploadedData];
                            updatedData[index]["Location Code"] =
                              e.target.value;
                            set_uploadedData(updatedData);
                          }}
                        />
                      )}
                    </td>
                    <td className="text-center">{data["Location*"]}</td>
                    <td className="text-center">{data["District"] || "-"}</td>
                    <td className="text-center">{data["State*"]}</td>
                    <td className="text-center">{data["Pincode"] || "-"}</td>
                    <td className="text-center">{data["Address"] || "-"}</td>
                    <td className="text-center">
                      <select
                        className="form-control"
                        onChange={(e) => LocationType(e, index)}
                        value={data["Location Type"] || "Destination"}
                      >
                        <option value="Destination">Destination</option>
                        <option value="Plant">Plant</option>
                        <option value="Warehouse">Warehouse</option>
                      </select>
                    </td>
                    <td className="text-center">
                      {data["Customer Name"] || "-"}
                    </td>
                    <td className="text-center">{data["GST No"] || "-"}</td>
                    <td
                      className={`text-center border-left`}
                      style={{
                        // position: "sticky",
                        // right: 0
                        backgroundColor: "white",
                      }}
                    >
                      <div style={{ position: "relative" }}>
                        <AutoComplete
                          key={`location_${index}`}
                          module_key="location_master"
                          user_identifier={company_id}

                          value={
                            data?.selected_location?.location_name
                              ? {
                                label: data.selected_location.location_name,
                                value: data.selected_location.location_name,
                              }
                              : null
                          }

                          placeholder="Please select a value from dropdown"

                          onChange={(val) => {
                            const updatedData = [..._uploadedData];
                            updatedData[index].selected_location = {
                              ...updatedData[index].selected_location,
                              location_name: val,   // typing
                            };
                            set_uploadedData(updatedData);
                          }}

                          onSelect={(selectedLocation) => {
                            const updatedData = [..._uploadedData];
                            updatedData[index].selected_location = selectedLocation; // full object
                            set_uploadedData(updatedData);
                          }}

                          searchFields="new_locations"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-center mt-3">
        <button
          className="btn btn-success mr-5"
          onClick={() => save_details(_uploadedData)}
        >
          Save
        </button>
      </div>
      <div className="text-center mt-3">
        <strong>Note: </strong>
        <span>Please Click on Save button to save the data after adding</span>
      </div>
    </>
  );
};

export default LocationDetails;